import {
  BasePaginateParamsSchema,
  BasePaginateResultSchema,
} from '@/api/common.ts'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { z } from 'zod'

dayjs.extend(isoWeek)

export const weeks = Array(53)
  .fill(0)
  .map((_, i) => `${i + 1}`)

export const defaultWeekFilter = {
  startDate: dayjs().startOf('isoWeek').toDate(),
  endDate: dayjs().startOf('isoWeek').add(3, 'week').endOf('isoWeek').toDate(),
  mfgDate: dayjs().toDate(),
}

export const Wo1110SearchParamsSchema = z
  .object({
    number: z.string().optional(),
    lotNo: z.string().optional(),
    startDate: z.coerce
      .date()
      .default(defaultWeekFilter.startDate)
      .transform((v) => dayjs(v).startOf('isoWeek').toDate()),
    endDate: z.coerce
      .date()
      .default(defaultWeekFilter.endDate)
      .transform((v) => {
        return dayjs(v).endOf('isoWeek').toDate()
      }),
    partyId: z.string().optional(),
    itemId: z.string().optional(),
    status: z
      .union([z.literal('1'), z.literal('2'), z.literal('3'), z.literal('4')])
      .optional(),
    mfgDate: z.coerce.date().default(defaultWeekFilter.mfgDate),
    mfgYear: z.number().default(dayjs().isoWeekYear()),
    mfgWeek: z.number().default(dayjs().isoWeek()),
    machine: z.string().optional(),
  })
  .merge(BasePaginateParamsSchema)
  .merge(z.object({ pageSize: z.number().optional().default(200000) }))

export type Wo1110SearchParams = z.infer<typeof Wo1110SearchParamsSchema>

export type Wo1110MfgSearchParams = Pick<
  Wo1110SearchParams,
  | 'mfgDate'
  | 'mfgYear'
  | 'mfgWeek'
  | 'machine'
  | 'pageSize'
  | 'itemId'
  | 'number'
>

export const Wo1110SearchResultModelSchema = z.object({
  id: z.number(),
  deliveryDate: z.coerce.date(),
  deliveryWeekNo: z.number().int(),
  productionDate: z.coerce.date(),
  mfgDate: z.coerce.date(),
  mfgYear: z.number().int(),
  mfgWeek: z.number().int(),
  partyId: z.number(),
  partyName: z.string(),
  itemId: z.number(),
  itemCode: z.string(),
  itemName: z.string(),
  qty: z.number(),
  number: z.string(),
  lotNo: z.string(),
  productionType: z.string().default(''),
  machine: z.string().default(''),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  status: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  isTestProduction: z.boolean(),
  durationInMinutes: z.number(),
  isManualDuration: z.boolean().default(false),
  hasMfgRecords: z.boolean().default(false),
  isBacklog: z.boolean().default(false),
  workOrderId: z.number(),
})

export type Wo1110SearchResultModel = z.infer<
  typeof Wo1110SearchResultModelSchema
>

export const Wo1110SearchResultSchema = z
  .object({
    data: z.array(Wo1110SearchResultModelSchema),
  })
  .merge(BasePaginateResultSchema)

export type Wo1110SearchResult = z.infer<typeof Wo1110SearchResultSchema>

// ----------------------  machine duration summary by year/week  --------------------------

export const Wo1110MachineDurationSearchSchema = z.object({
  mfgYear: z.number().default(dayjs().isoWeekYear()),
  mfgWeek: z.number().default(dayjs().isoWeek()),
})

export type Wo1110MachineDurationSearch = z.infer<
  typeof Wo1110MachineDurationSearchSchema
>

export const Wo1110MachineDurationResultModelSchema = z
  .object({
    code: z.string().default(''),
    durationInMinutes: z.number(),
  })
  .transform((values) => ({
    ...values,
    get id() {
      return `${values.code}`
    },
  }))

export type Wo1110MachineDurationResultModel = z.infer<
  typeof Wo1110MachineDurationResultModelSchema
>

export const Wo1110MachineDurationResultSchema = z.object({
  data: z.array(Wo1110MachineDurationResultModelSchema),
})

export type Wo1110MachineDurationResult = z.infer<
  typeof Wo1110MachineDurationResultSchema
>
